import { cn } from "@/lib/utils";
import useLayoutPreferencesStore from "@/store/useLayoutPreferencesStore";
import React from "react";

interface HeaderLayoutProps {
  children: React.ReactNode;
  /** Applied to the root layout div. */
  className?: string;
  /** When true (default), stack left/right in a column on small screens. When false, keep row layout at all breakpoints. */
  stackOnMobile?: boolean;
}
const HeaderLayout: React.FC<HeaderLayoutProps> = ({
  children,
  className,
  stackOnMobile = true,
}) => {
  const isSidebarVisible = useLayoutPreferencesStore(
    (state) => state.leftSidebarVisible,
  );

  const childrenArray = React.Children.toArray(children);
  const [leftContent, secondChild, ...restChildren] = childrenArray;

  // If we have 3+ children, use left, center, right layout
  // If we have 2 children, use left and right layout (no center)
  // If we have 1 child, use left only
  const hasCenter = childrenArray.length >= 3;
  const centerContent = hasCenter ? secondChild : null;
  const rightContent = hasCenter
    ? restChildren
    : secondChild
      ? [secondChild]
      : [];

  return (
    <div
      className={cn(
        "flex min-h-[54px] gap-4 px-7 py-5 border-b border-slate-100",
        stackOnMobile
          ? "flex-col sm:flex-row sm:justify-between items-stretch sm:items-center"
          : "flex-row justify-between items-center",
        className,
      )}
    >
      <div
        className={cn(
          "flex grow gap-3",
          stackOnMobile
            ? "flex-col sm:flex-row items-start sm:items-center sm:gap-0"
            : "flex-row items-center gap-0",
          !isSidebarVisible && "pl-3",
        )}
      >
        {leftContent}
      </div>
      {centerContent && (
        <div className="flex items-center justify-center shrink-0">
          {centerContent}
        </div>
      )}
      {rightContent.length > 0 && (
        <div className="flex items-center grow justify-end">{rightContent}</div>
      )}
    </div>
  );
};

export default HeaderLayout;
